import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { GridColDef } from "@mui/x-data-grid";
import { format } from "date-fns";
import { ptBR as dateFnsPtBR, enUS, es } from "date-fns/locale";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GlobalAxios } from "../../../../axios/axios-config";
import TableList from "../../../../components/TableList/TableList";
import {
  IPatientEncounterPsTransferRequest,
  IPatientEncounterPsTransferRequestLog,
} from "../../../../models/patient-encounter-ps-transfer-request";

const PATIENT_ENCOUNTER_PS_TRANSFER_REQUEST_LOGS_ROUTE =
  process.env.REACT_APP_EXTERNAL + "/api/v1/PatientEncounterPsTransferRequest/Logs";

interface IPatientEncounterPsTransferRequestDetailsModalProps {
  patientEncounterPsTransferRequest: IPatientEncounterPsTransferRequest;
  onClose: () => void;
}

const translationPath = "page.4uBus.logs.patientEncounterPsTransferRequest.";

const statusConfig: Record<
  string,
  { color: "info" | "warning" | "success" | "error"; key: string }
> = {
  PENDING: { color: "info", key: "pending" },
  PROCESSING: { color: "warning", key: "processing" },
  SUCCESS: { color: "success", key: "success" },
  MANUAL_REVIEW: { color: "error", key: "manual_review" },
};

const Field = ({
  label,
  value,
}: {
  label: string;
  value?: string | number | null;
}) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ display: "block", wordBreak: "break-word" }}>
      {value ?? "-"}
    </Typography>
  </Grid>
);

const PatientEncounterPsTransferRequestDetailsModal: React.FC<
  IPatientEncounterPsTransferRequestDetailsModalProps
> = ({ patientEncounterPsTransferRequest, onClose }) => {
  const { t, i18n } = useTranslation();
  const [logData, setLogData] = useState<IPatientEncounterPsTransferRequestLog[]>(
    [],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await GlobalAxios.get(
          PATIENT_ENCOUNTER_PS_TRANSFER_REQUEST_LOGS_ROUTE,
          {
            params: {
              PatientEncounterPsTransferRequestId: patientEncounterPsTransferRequest.id,
            },
          },
        );

        setLogData(response.data ?? []);
      } catch (error) {
        console.error("Erro ao buscar logs:", error);
      }
    };

    fetchData();
  }, [patientEncounterPsTransferRequest.id]);

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "-";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";

    return format(date, "Pp", {
      locale:
        i18n.language === "pt-BR"
          ? dateFnsPtBR
          : i18n.language === "en-US"
            ? enUS
            : es,
    }).replaceAll(",", "");
  };

  const payload = useMemo(() => {
    if (!patientEncounterPsTransferRequest.jsonContent) return "-";
    try {
      return JSON.stringify(
        JSON.parse(patientEncounterPsTransferRequest.jsonContent),
        null,
        2,
      );
    } catch {
      return patientEncounterPsTransferRequest.jsonContent;
    }
  }, [patientEncounterPsTransferRequest.jsonContent]);

  const statusValue = patientEncounterPsTransferRequest.statusSend;
  const statusCfg = (statusValue && statusConfig[statusValue]) || {
    color: "info" as const,
    key: "pending",
  };

  const columns: GridColDef<IPatientEncounterPsTransferRequestLog>[] = [
    {
      field: "success",
      headerName: t(translationPath + "details.success") || "Status",
      width: 130,
      renderCell: (params) => (
        <Chip
          label={
            params.value
              ? t(translationPath + "details.success_true") || "Sucesso"
              : t(translationPath + "details.success_false") || "Falha"
          }
          color={params.value ? "success" : "error"}
          size="small"
        />
      ),
    },
    {
      field: "created",
      headerName: t(translationPath + "details.created") || "Data",
      width: 180,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "logData",
      headerName: t(translationPath + "details.log_data") || "Dados do Log",
      flex: 1,
      minWidth: 320,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: "monospace",
            fontSize: "0.75rem",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            minHeight: 52,
            display: "flex",
            alignItems: "center",
            py: 1,
          }}
        >
          {params.value || "-"}
        </Typography>
      ),
    },
  ];

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {t(translationPath + "details.title") || "Detalhes da Transferência"} -{" "}
        {patientEncounterPsTransferRequest.originalPatientEncounterNumber}
      </DialogTitle>
      <DialogContent dividers>
        <Stack gap={2}>
          <Grid container spacing={2}>
            <Field
              label={t(translationPath + "original_patient_encounter_number")}
              value={patientEncounterPsTransferRequest.originalPatientEncounterNumber}
            />
            <Field
              label={t(translationPath + "new_patient_encounter_number")}
              value={patientEncounterPsTransferRequest.newPatientEncounterNumber}
            />
            <Field
              label={t(translationPath + "new_prescription_number")}
              value={patientEncounterPsTransferRequest.newPrescriptionNumber}
            />
            <Field
              label={t(translationPath + "individual_beneficiary_name")}
              value={patientEncounterPsTransferRequest.individualBeneficiaryName}
            />
            <Field
              label={t(translationPath + "individual_beneficiary_id")}
              value={patientEncounterPsTransferRequest.individualBeneficiaryIntegrationId}
            />
            <Field
              label={t(translationPath + "original_doctor_integration_id")}
              value={patientEncounterPsTransferRequest.originalDoctorIntegrationId}
            />
            <Field
              label={t(translationPath + "target_doctor_integration_id")}
              value={patientEncounterPsTransferRequest.targetDoctorIntegrationId}
            />
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" color="text.secondary">
                {t(translationPath + "status")}
              </Typography>
              <Stack direction="row">
                <Chip
                  label={t(translationPath + `status_values.${statusCfg.key}`)}
                  color={statusCfg.color}
                  size="small"
                />
              </Stack>
            </Grid>
            <Field
              label={t(translationPath + "retries")}
              value={patientEncounterPsTransferRequest.retries}
            />
            <Field
              label={t(translationPath + "created")}
              value={formatDate(patientEncounterPsTransferRequest.created)}
            />
            <Field
              label={t(translationPath + "details.last_attempt_date")}
              value={formatDate(patientEncounterPsTransferRequest.lastAttemptDate)}
            />
            <Field
              label={t(translationPath + "transfer_confirmed_date")}
              value={formatDate(patientEncounterPsTransferRequest.transferConfirmedDate)}
            />
          </Grid>

          {patientEncounterPsTransferRequest.statusMessage && (
            <Alert severity="error">
              {patientEncounterPsTransferRequest.statusMessage}
            </Alert>
          )}

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={500}>
                {t(translationPath + "details.payload") || "Payload"}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                component="pre"
                variant="body2"
                sx={{
                  m: 0,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontFamily: "monospace",
                }}
              >
                {payload}
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Typography variant="h6">
            {t(translationPath + "details.history") || "Histórico"}
          </Typography>
          <TableList
            columns={columns}
            rows={logData}
            fileName={`patient-encounter-ps-transfer-request-logs-${patientEncounterPsTransferRequest.originalPatientEncounterNumber}`}
            showSelects={false}
            onRowSelectionModelChange={undefined}
            pageSizeOptions={[5, 10, 25, 50]}
            hideTitle
            getRowHeight={() => "auto"}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientEncounterPsTransferRequestDetailsModal;
